import { useCallback, useEffect, useState } from 'react';

import { telemetryDelegateWrapper } from '../services/telemetry/telemetry-delegate-wrapper';
import { telemetryModelUpdateWrapper } from '../services/telemetry/telemetry-model-update-wrapper';

type ControllerInstance<C> = C & {
  dispose?(): void;
};

interface ControllerWithDispose<M, C> {
  new (setModel: (model: M) => void, initialModel?: M): ControllerInstance<C>;
}

// Global storage for shared models and controllers
const globalModels = new Map<unknown, unknown>();
const globalControllers = new Map<unknown, unknown>();
const globalListeners = new Map<unknown, Set<(model: any) => void>>();

function initialiseController<M, C>(
  Controller: ControllerWithDispose<M, C>,
  config: { initialModel: () => M; setModel: (newModel: M) => void; useTelemetry?: boolean }
) {
  const { initialModel, setModel, useTelemetry } = config;

  if (!globalModels.has(Controller)) {
    globalModels.set(Controller, initialModel());
  }

  if (!globalListeners.has(Controller)) {
    globalListeners.set(Controller, new Set());
  }

  let controller = new Controller(setModel, globalModels.get(Controller) as M);
  if (useTelemetry) {
    // Optionally wraps controller with telemetry
    controller = telemetryDelegateWrapper(Controller.name, ['*'], controller)!;
  }

  setGlobalController(Controller, controller);
  return controller;
}

function getListeners<M, C>(Controller: ControllerWithDispose<M, C>): Set<(model: M) => void> | undefined {
  if (!globalListeners.has(Controller)) {
    globalListeners.set(Controller, new Set());
  }
  return globalListeners.get(Controller);
}

function addListener<M, C>(Controller: ControllerWithDispose<M, C>, listener: (model: M) => void) {
  getListeners(Controller)?.add(listener);
}

function removeListener<M, C>(Controller: ControllerWithDispose<M, C>, listener: (model: M) => void) {
  const listeners = getListeners(Controller);
  listeners?.delete(listener);
  return listeners?.size ?? 0;
}

function setGlobalModel<M, C>(Controller: ControllerWithDispose<M, C>, model: M) {
  globalModels.set(Controller, model);
}

function getGlobalModel<M, C>(Controller: ControllerWithDispose<M, C>) {
  return globalModels.get(Controller) as M;
}

function setGlobalController<M, C>(Controller: ControllerWithDispose<M, C>, controller: ControllerInstance<C>) {
  globalControllers.set(Controller, controller);
}

function getGlobalController<M, C>(Controller: ControllerWithDispose<M, C>) {
  return globalControllers.get(Controller) as ControllerInstance<C>;
}

/**
 * @deprecated in favour of @tanstack/react-query for server state and controller-less hooks like createSharedState and useSharedState for UI state.
 *
 * Custom hook to manage a shared model and its controller.
 * With this hook, when you need a global model you don't need to instantiate it in App root, you can just call this hook from any component.
 *
 * Note that although the hook only stores one instance per Controller class,
 * it is possible that one controller is disposed, after there are no more listeners, and a new one is created later which replaces the previous one.
 * This is more likely to happen during development in strict mode, where useEffect's are called twice.
 *
 * Features:
 * - Multiple components using the same Controller class will share the same model instance.
 * - When the model is updated, all components are notified and re-rendered.
 * - A controller instance is disposed when no more components (listeners) are using it.
 * - Optionally, telemetry can be enabled to log model changes and controller actions.
 */
export function useSharedModelController<M, C>(
  initialModel: () => M,
  Controller: ControllerWithDispose<M, C>,
  options?: { useTelemetry?: boolean }
) {
  const { useTelemetry = false } = options || {};

  // Global state updater that notifies all listeners
  const setModel = useCallback(
    (newModel: M) => {
      setGlobalModel(Controller, newModel);
      getListeners(Controller)?.forEach((listener) => listener(newModel));
    },
    [Controller]
  );

  const [delegate, setDelegate] = useState<C>(
    () => getGlobalController(Controller) ?? initialiseController(Controller, { initialModel, setModel, useTelemetry })
  );

  // Changing globalModel would not trigger a re-render so
  // we keep a copy of the model with useState which triggers re-renders when updated.
  const [modelLocal, setModelLocal] = useState<M>(() => getGlobalModel(Controller) ?? initialModel());

  const setTelemetryLoggerMemo = useCallback(() => telemetryModelUpdateWrapper<M>(Controller.name, ['*']), [Controller]);

  const [telemetryLogger, setTelemetryLogger] = useState<((delta: M) => void) | undefined>(
    useTelemetry ? setTelemetryLoggerMemo() : undefined
  );

  // Subscribe to global model changes
  useEffect(() => {
    const updateLocal = (newModel: M) => {
      setModelLocal(newModel);
    };

    if (!globalControllers.has(Controller)) {
      const controller = initialiseController(Controller, { initialModel, setModel, useTelemetry });
      setDelegate(controller);
    }

    addListener(Controller, updateLocal);

    // ensure the listener we just added gets the current model
    const currentModel = getGlobalModel<M, C>(Controller);
    if (currentModel !== undefined) {
      setModelLocal(currentModel);
    }

    if (useTelemetry) {
      // Setup telemetry logger for model changes
      setTelemetryLogger(telemetryModelUpdateWrapper<M>(Controller.name, ['*']));
    }

    return () => {
      const listenersSize = removeListener(Controller, updateLocal);

      // Defer disposal to a microtask. React runs all cleanups and new effects in one
      // synchronous batch, so any component that is going to re-register will have done
      // so before this fires. If there are still no listeners at that point, dispose.
      if (listenersSize === 0) {
        void Promise.resolve().then(() => {
          if (globalListeners.get(Controller)?.size !== 0) {
            return;
          }

          const controller = getGlobalController<M, C>(Controller);
          controller?.dispose?.();

          globalControllers.delete(Controller);
          globalModels.delete(Controller);
          globalListeners.delete(Controller);
        });
      }
    };
  }, [Controller, initialModel, setModel, useTelemetry]);

  // Telemetry for model changes
  useEffect(() => {
    if (useTelemetry && telemetryLogger) {
      telemetryLogger(modelLocal);
    }
  }, [telemetryLogger, modelLocal, useTelemetry]);

  return { model: modelLocal, delegate };
}
